import React, { useState, useEffect } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import servicelogo from '../../assets/anonymousListingImages.jpeg';
// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_CLOSED,
  propTypes,
} from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors.js';
import {
  hasPermissionToViewData,
  isUserAuthorized,
} from '../../util/userHelpers.js';
import { requireListingImage } from '../../util/configHelpers';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import {
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

// Global ducks (for Redux actions and thunks)
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  manageDisableScrolling,
  isScrollingDisabled,
} from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';

// Shared components
import {
  H4,
  H3,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
  Breadcrumbs,
  LimitReachedModal,
  Button,
  ListingCard,
  Avatar,
  UserDisplayName,
  ReviewRating,
} from '../../components';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmit,
} from './ListingPage.shared';
import ActionBarMaybe from './ActionBarMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionReviews from './SectionReviews';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import SectionMapMaybe from './SectionMapMaybe';
import SectionGallery from './SectionGallery';
import CustomListingFields from './CustomListingFields';

import css from './ListingPage.module.css';
import IconCard from '../../components/SavedCardDetails/IconCard/IconCard.js';
import MaterialDetails from '../../components/MaterialDetails/MaterialDetails.js';
import FeaturedListings from '../../components/FeaturedListings/FeaturedListings.js';
import MoreListings from './MoreListingsShow/MoreListings.js';
import verfiedIcon from '../../assets/verifiedIcon.png';
import { sendMessage } from '../CheckoutPage/CheckoutPage.duck.js';
import { productSchema } from '../../util/listingSchema';
import { breadcrumbForListing } from '../../util/breadcrumbSchema';
const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID } = sdkTypes;

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [imageCarouselOpen, setImageCarouselOpen] = useState(false);
  const [limitReachedModalOpen, setLimitReachedModalOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [expandedReviews, setExpandedReviews] = useState({});
  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    showListingInProgress,
    reviews = [],
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    moreListings,
    fetchMoreListingsInProgress,
    fetchMoreListingsError,
    routeConfiguration,
    showOwnListingsOnly,
    onSendMessage,
    ...restOfProps
  } = props;
  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isVariant = rawParams.variant != null;
  const isPendingApprovalVariant =
    rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));



  const listingSlug =
    rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id &&
    currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage =
    pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return (
      <NamedRedirect
        name="ListingPage"
        params={params}
        search={location.search}
      />
    );
  }

  const topbar = (
    <TopbarContainer
      showMobileBackButton
      mobileBackButtonFallbackPath="/s?publicData.installer_or_listing=listing"
    />
  );
  const moreListingsToDisplay = moreListings?.filter(listing => {
    const isCurrentListing = listing?.id?.uuid !== currentListing?.id?.uuid;
    const isAnonymous = listing?.attributes?.publicData?.anonymousListing;
    return isCurrentListing && !isAnonymous;
  });

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    // Other error in fetching listing
    return (
      <ErrorPage
        topbar={topbar}
        scrollingDisabled={scrollingDisabled}
        intl={intl}
      />
    );
  } else if (!currentListing.id) {
    // Still loading the listing
    return (
      <LoadingPage
        topbar={topbar}
        scrollingDisabled={scrollingDisabled}
        intl={intl}
      />
    );
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable &&
    currentListing.author.id.uuid === currentUser.id.uuid;
  const { is_verified_company } = currentUser?.attributes?.profile?.publicData || {};
  const {
    listingType,
    transactionProcessAlias,
    unitType,
    anonymousListing,
    serviceLocations = [],
  } = publicData;

  const addresses = serviceLocations
    .map(loc => loc?.location?.selectedPlace?.address)
    .filter(Boolean);
  const addressDisplay = addresses.length > 0 ? addresses.join(' | ') : '';
  if (!(listingType && transactionProcessAlias && unitType)) {
    // Listing should always contain listingType, transactionProcessAlias and unitType)
    return (
      <ErrorPage
        topbar={topbar}
        scrollingDisabled={scrollingDisabled}
        intl={intl}
        invalidListing
      />
    );
  }
  const validListingTypes = listingConfig.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(
    conf => conf.listingType === listingType
  );
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const processName = resolveLatestProcessName(
    transactionProcessAlias.split('/')[0]
  );
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const processType = isBooking
    ? 'booking'
    : isPurchase
      ? 'purchase'
      : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing &&
    (processType !== 'inquiry' && !currentUser?.attributes?.stripeConnected);
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage
        id="ListingPage.payoutDetailsWarning"
        values={{ processType }}
      />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  // Note: this is for inquiry state in booking and purchase processes. Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
    setLimitReachedModalOpen,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    location,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
    setLimitReachedModalOpen,
    onSendMessage,
  });

  const handleOrderSubmit = values => {
    const isCurrentlyClosed =
      currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      return onSubmit(values);
    }
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );

  // Meta description: truncate to 155 chars for Google snippet, with fallback
  const metaDescription = description
    ? description.length > 155
      ? description.substring(0, 152).trim() + '...'
      : description
    : `${title}${formattedPrice ? ` — ${formattedPrice}` : ''} | Buy pallet racking on ${marketplaceName}`;

  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const REVIEWS_PREVIEW_COUNT = 3;
  const hasMoreReviews = reviews.length > REVIEWS_PREVIEW_COUNT;
  const visibleReviews = showAllReviews ? reviews : reviews.slice(0, REVIEWS_PREVIEW_COUNT);

  const toggleReviewExpansion = reviewId => {
    setExpandedReviews(prev => ({
      ...prev,
      [reviewId]: !prev[reviewId],
    }));
  };
  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={metaDescription}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={[
        productSchema({
          listing: currentListing,
          productURL,
          description: metaDescription,
          images: schemaImages,
        }),
        breadcrumbForListing({
          publicData,
          categories: config?.categoryConfiguration?.categories,
          rootURL: config.marketplaceRootURL,
          listingURL: productURL,
          listingTitle: title,
          marketplaceName: config.marketplaceName,
        }),
      ].filter(Boolean)}
    >
      <LayoutSingleColumn
        className={css.pageRoot}
        topbar={topbar}
        footer={<FooterContainer />}
      >
        <div className={css.pageWrapper}>
          <Breadcrumbs
            className={css.breadcrumbs}
            publicData={publicData}
            categories={config?.categoryConfiguration?.categories}
            currentPageLabel={title}
            rootLabel={config.marketplaceName}
          />
          {/* {mounted && currentListing.id && noPayoutDetailsSetWithOwnListing ? (
            <ActionBarMaybe
              className={css.actionBarForProductLayout}
              isOwnListing={isOwnListing}
              listing={currentListing}
              showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
              currentUser={currentUser}
            />
          ) : null} */}
          {mounted && currentListing.id ? (
            <ActionBarMaybe
              className={css.actionBarForProductLayout}
              isOwnListing={isOwnListing}
              listing={currentListing}
              currentUser={currentUser}
              editParams={{
                id: listingId.uuid,
                slug: listingSlug,
                type: listingPathParamType,
                tab: listingTab,
              }}
            />
          ) : null}
          {/* {top title container} */}
          <div className={css.titleWrapper}>
            <div className={css.titleContainer}>
              <div className={css.titleText}>{title}</div>
              <div className={css.locationText}>
                <span><FormattedMessage id="ListingPage.yardLocation" />:</span> {addressDisplay}
              </div>
              <div className={css.titlePrice}>
                {/* Price and unit are one flex child, so the container's 20px
                    gap applies only before the verified badge — not between
                    the amount and "/ each". */}
                <span className={css.priceAmount}>
                  {formattedPrice}
                  <span className={css.priceUnit}>
                    <FormattedMessage id="ListingPage.pricePerEach" />
                  </span>
                </span>
                {is_verified_company && <span className={css.verfiedIcon}>
                  <img src={verfiedIcon} />
                </span>}
              </div>
              {/* Freight is quoted separately and can rival the material cost,
                  so the listed price must not read as a delivered price. */}
              <div className={css.priceSublabel}>
                <FormattedMessage id="ListingPage.priceSublabel" />
              </div>
            </div>
            {!isOwnListing && (
              <Button
                className={css.messageButton}
                onClick={() => onContactUser()}
              >
                <IconCard brand="message" />
                <FormattedMessage id="ListingPage.messageDealer" />
              </Button>
            )}
          </div>

          <div className={css.contentWrapperForProductLayout}>
            <div className={css.mainColumnForProductLayout}>
              {showListingImage && (
                <SectionGallery
                  listing={currentListing}
                  variantPrefix={config.layout.listingImage.variantPrefix}
                />
              )}
            </div>
            <div className={css.orderColumnForProductLayout}>
              <OrderPanel
                className={classNames(css.productOrderPanel, {
                  [css.imagesEnabled]: showListingImage,
                })}
                listing={currentListing}
                isOwnListing={isOwnListing}
                onSubmit={handleOrderSubmit}
                authorLink={
                  <NamedLink
                    className={css.authorNameLink}
                    name={isVariant ? 'ListingPageVariant' : 'ListingPage'}
                    params={params}
                    to={{ hash: '#author' }}
                  >
                    {authorDisplayName}
                  </NamedLink>
                }
                title={
                  <FormattedMessage
                    id="ListingPage.orderTitle"
                    values={{ title: richTitle }}
                  />
                }
                titleDesktop={
                  <H4 as="h1" className={css.orderPanelTitle}>
                    <FormattedMessage
                      id="ListingPage.orderTitle"
                      values={{ title: richTitle }}
                    />
                  </H4>
                }
                payoutDetailsWarning={payoutDetailsWarning}
                author={ensuredAuthor}
                onManageDisableScrolling={onManageDisableScrolling}
                onContactUser={onContactUser}
                alwaysVisibleOnMobile={true}
                {...restOfProps}
                validListingTypes={config.listing.listingTypes}
                marketplaceCurrency={config.currency}
                dayCountAvailableForBooking={
                  config.stripe.dayCountAvailableForBooking
                }
                marketplaceName={config.marketplaceName}
                showListingImage={showListingImage}
              />
            </div>
          </div>
          <div
            className={
              showListingImage
                ? css.mobileHeading
                : css.noListingImageHeadingProduct
            }
          >
            {showListingImage ? (
              <H4 as="h1" className={css.orderPanelTitle}>
                <FormattedMessage
                  id="ListingPage.orderTitle"
                  values={{ title: richTitle }}
                />
              </H4>
            ) : (
              <H3 as="h1" className={css.orderPanelTitle}>
                <FormattedMessage
                  id="ListingPage.orderTitle"
                  values={{ title: richTitle }}
                />
              </H3>
            )}
          </div>
          <SectionTextMaybe text={description} showAsIngress />

          {/* <CustomListingFields
              publicData={publicData}
              metadata={metadata}
              listingFieldConfigs={listingConfig.listingFields}
              categoryConfiguration={config.categoryConfiguration}
              intl={intl}
            /> */}
          {listingType === 'complete_system' &&
            Array.isArray(publicData?.componentsArray) &&
            publicData.componentsArray.length > 0 ? (
            publicData.componentsArray.map((componentData, index) => (
              <MaterialDetails key={index} publicData={componentData} stockLoading={showListingInProgress} categories={config?.categoryConfiguration?.categories} />
            ))
          ) : (
            <MaterialDetails publicData={publicData} stockLoading={showListingInProgress} categories={config?.categoryConfiguration?.categories} />
          )}

          <SectionMapMaybe
            geolocation={geolocation}
            publicData={publicData}
            listingId={currentListing.id}
            mapsConfig={config.maps}
          />

          <SectionAuthorMaybe
            title={title}
            listing={currentListing}
            authorDisplayName={authorDisplayName}
            onContactUser={onContactUser}
            anonymousListing={anonymousListing}
            isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
            onCloseInquiryModal={() => setInquiryModalOpen(false)}
            sendInquiryError={sendInquiryError}
            sendInquiryInProgress={sendInquiryInProgress}
            onSubmitInquiry={onSubmitInquiry}
            currentUser={currentUser}
            onManageDisableScrolling={onManageDisableScrolling}
          />
          {moreListingsToDisplay && moreListingsToDisplay.length > 0 && !anonymousListing ? (
            <MoreListings listings={moreListingsToDisplay} config={config} intl={intl} />
          ) : (
            null
          )}
          {/* {reviews && reviews.length > 0 && <SectionReviews
            reviews={reviews}
            fetchReviewsError={fetchReviewsError}
          />} */}
          {reviews.length > 0 && <section className={css.reviewsSection}>
            <h2 className={css.reviewTitle}>Reviews and Ratings</h2>
            <div
              className={`${css.reviewsContainer} ${showAllReviews ? css.showAll : ''
                }`}
            >
              {visibleReviews.map((review, index) => {
                const reviewerCompany =
                  review?.author?.attributes?.profile?.publicData?.companyName ||
                  review?.author?.attributes?.profile?.publicData?.businessName;
                const content = review?.attributes?.content || '';
                const reviewKey = review?.id?.uuid
                  ? `Review_${review.id.uuid}`
                  : `Review_fallback_${index}`;
                const isLongContent = content && content.length > 200;
                const isExpanded = !!expandedReviews[reviewKey];

                return (
                  <div key={reviewKey} className={css.reviewCard}>
                    <div className={css.reviewHeader}>
                      <div className={css.reviewerInfo}>
                        <div className={css.reviewImg}>
                          <Avatar user={review.author} />
                        </div>
                        <div>
                          <div className={css.reviewerName}>
                            <UserDisplayName user={review.author} intl={intl} />
                          </div>
                          {reviewerCompany && !anonymousListing ? (
                            <div className={css.businessName}>{reviewerCompany}</div>
                          ) : null}
                        </div>
                      </div>
                      <div className={css.rating}>
                        <ReviewRating rating={review?.attributes?.rating} reviewStarClassName={css.star} />
                      </div>
                    </div>
                    <p
                      className={classNames(css.reviewText, {
                        [css.reviewTextCollapsed]: isLongContent && !isExpanded,
                      })}
                    >
                      {content}
                    </p>
                    {isLongContent && (
                      <button
                        type="button"
                        className={css.seeMoreButtonSecond}
                        onClick={() => toggleReviewExpansion(reviewKey)}
                      >
                        {isExpanded ? 'Show less' : 'See more'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            {hasMoreReviews && (
              <button
                onClick={() => setShowAllReviews(prev => !prev)}
                className={css.seeMoreButton}
              >
                {showAllReviews ? 'load Less' : 'load More'}
              </button>
            )}
          </section>}
        </div>
        <LimitReachedModal
          isOpen={limitReachedModalOpen}
          onClose={() => setLimitReachedModalOpen(false)}
          onManageDisableScrolling={onManageDisableScrolling}
        />
        {/* Fixed Message Dealer Button - Mobile Only */}
        <div className={css.messageDealerButtonContainer}>
          <button
            className={css.messageDealerButton}
            onClick={() => onContactUser()}
          >
            <IconCard brand="message" />
            <FormattedMessage id="ListingPage.messageDealer" />
          </button>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

/**
 * The ListingPage component with carousel layout.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.params - The path params object
 * @param {string} props.params.id - The listing id
 * @param {string} props.params.slug - The listing slug
 * @param {LISTING_PAGE_DRAFT_VARIANT | LISTING_PAGE_PENDING_APPROVAL_VARIANT} props.params.variant - The listing variant
 * @param {Function} props.onManageDisableScrolling - The on manage disable scrolling function
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated
 * @param {Function} props.getListing - The get listing function
 * @param {Function} props.getOwnListing - The get own listing function
 * @param {Object} props.currentUser - The current user
 * @param {boolean} props.scrollingDisabled - Whether scrolling is disabled
 * @param {string} props.inquiryModalOpenForListingId - The inquiry modal open for the specific listing id
 * @param {propTypes.error} props.showListingError - The show listing error
 * @param {Function} props.callSetInitialValues - The call setInitialValues function, which is given to this function as a parameter
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.fetchReviewsError - The fetch reviews error
 * @param {Object<string, Object>} props.monthlyTimeSlots - The monthly time slots. E.g. { '2019-11': { timeSlots: [], fetchTimeSlotsInProgress: false, fetchTimeSlotsError: null } }
 * @param {Object<string, Object>} props.timeSlotsForDate - The time slots for date. E.g. { '2019-11-01': { timeSlots: [], fetchedAt: 1572566400000, fetchTimeSlotsError: null, fetchTimeSlotsInProgress: false } }
 * @param {boolean} props.sendInquiryInProgress - Whether the send inquiry is in progress
 * @param {propTypes.error} props.sendInquiryError - The send inquiry error
 * @param {Function} props.onSendInquiry - The on send inquiry function
 * @param {Function} props.onInitializeCardPaymentData - The on initialize card payment data function
 * @param {Function} props.onFetchTimeSlots - The on fetch time slots function
 * @param {Function} props.onFetchTransactionLineItems - The on fetch transaction line items function
 * @param {Array<propTypes.transactionLineItem>} props.lineItems - The line items
 * @param {boolean} props.fetchLineItemsInProgress - Whether the fetch line items is in progress
 * @param {propTypes.error} props.fetchLineItemsError - The fetch line items error
 * @returns {JSX.Element} listing page component
 */
const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant != null;
  const currentUser = props.currentUser;
  if (isForbiddenError(showListingError) && !isVariant && !currentUser) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{
          from: `${location.pathname}${location.search}${location.hash}`,
        }}
      />
    );
  }

  const isPrivateMarketplace =
    config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRights =
    currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(
    showListingError
  );

  if (
    (isPrivateMarketplace && isUnauthorizedUser) ||
    hasUserPendingApprovalError
  ) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (hasNoViewingRights && isForbiddenError(showListingError)) ||
    isErrorNoViewingPermission(showListingError)
  ) {
    // If the user has no viewing rights, fetching anything but their own listings
    // will return a 403 error. If that happens, redirect to NoAccessPage.
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      showOwnListingsOnly={hasNoViewingRights}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    showListingInProgress,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    timeSlotsForDate,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    moreListings,
    fetchMoreListingsInProgress,
    fetchMoreListingsError,
    inquiryModalOpenForListingId,
  } = state.ListingPage;
  const { currentUser } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    showListingInProgress,
    reviews,
    moreListings,
    fetchMoreListingsInProgress,
    fetchMoreListingsError,
    fetchReviewsError,
    monthlyTimeSlots, // for OrderPanel
    timeSlotsForDate, // for OrderPanel
    lineItems, // for OrderPanel
    fetchLineItemsInProgress, // for OrderPanel
    fetchLineItemsError, // for OrderPanel
    sendInquiryInProgress,
    sendInquiryError,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params =>
    dispatch(fetchTransactionLineItems(params)), // for OrderPanel
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone, options) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone, options)), // for OrderPanel
  onSendMessage: params => dispatch(sendMessage(params)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedListingPage);

export default ListingPage;
